// Inline export removed (legacy file deleted). Bring current implementation by re-importing from app export if needed.
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ShoppingCart as CartIcon, Trash2, ArrowLeft, CreditCard, Package, Plus, Minus } from 'lucide-react';
import { useCart } from '../core/cart/CartContext';
import { useCheckout } from '../core/hooks/useCheckout';
import { formatPrice } from '../../utils/helpers';
import { SHIPPING_CONFIG } from '../../utils/constants';
import { Loading } from '../../components/ui/Loading';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import { ProductCard } from '../ui/ProductCard';
import { WooCommerceDataProvider } from '../adapters/catalog/woocommerce';
import type { Product } from '../core/ports';

interface CartProps { className?: string }

export default function Cart({ className = '' }: CartProps) {
  const { cart, updateCartItem, removeFromCart, clearCart, error: cartError, clearError } = useCart();
  const { loading: checkoutLoading, error: checkoutError } = useCheckout();
  const navigate = useNavigate();
  const [updatingItem, setUpdatingItem] = useState<number | null>(null);
  const [crossSellProducts, setCrossSellProducts] = useState<Product[]>([]);
  const [loadingCrossSells, setLoadingCrossSells] = useState(false);

  // Calculate shipping cost based on order total
  const FREE_SHIPPING_THRESHOLD = SHIPPING_CONFIG.FREE_SHIPPING_THRESHOLD;
  const hasPreOrderItems = cart.items.some((item: any) => item.isPreOrder);
  const getShippingCost = () => {
    if (cart.total >= FREE_SHIPPING_THRESHOLD) {
      return { cost: 0, text: 'Free', color: 'text-green-500' };
    } else {
      return { cost: 0, text: 'TBD', color: 'text-yellow-500' }; // Will be calculated at checkout
    }
  };

  const shipping = getShippingCost();


  // Fetch cross-sell products for items in cart
  useEffect(() => {
    const fetchCrossSells = async () => {
      if (cart.items.length === 0) {
        setCrossSellProducts([]);
        return;
      }

      setLoadingCrossSells(true);
      try {
        const allCrossSells: Product[] = [];
        
        // Fetch cross-sells for each product in cart
        for (const item of cart.items) {
          if (WooCommerceDataProvider.getLinkedProducts) {
            try {
              const response = await WooCommerceDataProvider.getLinkedProducts(item.productId);
              if (response && response.crossSells) {
                allCrossSells.push(...response.crossSells);
              }
            } catch (err) {
              console.error('Error fetching cross-sells for product:', item.productId, err);
            }
          }
        }

        // Remove duplicates and limit to 4 products
        const uniqueCrossSells = allCrossSells.filter((product, index, self) => 
          index === self.findIndex(p => p.id === product.id) &&
          !cart.items.some(cartItem => cartItem.productId === product.id)
        ).slice(0, 4);

        setCrossSellProducts(uniqueCrossSells);
      } catch (err) {
        console.error('Error fetching cross-sell products:', err);
      } finally {
        setLoadingCrossSells(false);
      }
    };

    fetchCrossSells();
  }, [cart.items]);

  const handleQuantityChange = async (productId: number, newQuantity: number) => {
    if (newQuantity <= 0) { removeFromCart(productId); return; }
    setUpdatingItem(productId);
    try { updateCartItem(productId, newQuantity); } finally { setUpdatingItem(null); }
  };

  const handleCheckout = () => {
    if (cart.items.length === 0) return;
    window.location.href = '/checkout';
  };

  if (cart.items.length === 0) {
    return (
      <div className={`min-h-screen bg-primary text-tertiary ${className}`}>
        <Header />
        <div className="h-20" />
        <Helmet><title>Cart - Oracle Gaming</title><meta name="description" content="Your shopping cart" /></Helmet>
        <section className="gallery-carousel" style={{ paddingTop: '120px', paddingBottom: '30px' }}>
          <div className="container">
            <h2 className="h2 section-title" style={{ color: 'var(--white)' }}>YOUR CART</h2>
            <div className="bg-primarySupport" style={{ padding: '40px 24px', textAlign: 'center' }}>
              <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6"><CartIcon className="w-12 h-12" color="#14fefb" /></div>
              <h3 className="h3" style={{ marginBottom: 10, color: 'var(--white)' }}>YOUR CART IS EMPTY</h3>
              <p style={{ color: 'var(--platinum)', marginBottom: 20 }}>Start adding some amazing products to your cart!</p>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}>
                <Link to="/shop" className="btn btn-primary" style={{ display: 'inline-flex' }}>
                  <CartIcon className="w-5 h-5" /> <span>Start Shopping</span>
                </Link>
              </div>
              <Link to="/shop" className="btn-link" style={{ color: 'var(--orange)' }}>
                <ArrowLeft className="w-5 h-5" /> Continue Shopping
              </Link>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-primary text-tertiary ${className}`}>
      <Helmet>
        <title>Shopping Cart - Pokemon & One Piece Cards | Oracle Gaming South Africa</title>
        <meta name="description" content="Review your Pokemon and One Piece card purchases. Free shipping over R1899. Secure checkout with fast delivery across South Africa." />
        <meta name="keywords" content="shopping cart, Pokemon cards cart, One Piece cards cart, TCG cart South Africa, checkout, free shipping, secure payment" />
        <meta property="og:title" content="Shopping Cart - Pokemon & One Piece Cards | Oracle Gaming South Africa" />
        <meta property="og:description" content="Review your Pokemon and One Piece card purchases. Free shipping over R1899. Secure checkout with fast delivery across South Africa." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://oracle-gaming.vercel.app/cart" />
        <meta property="og:image" content="https://oracle-gaming.vercel.app/images/logo.svg" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Shopping Cart - Pokemon & One Piece Cards | Oracle Gaming South Africa" />
        <meta name="twitter:description" content="Review your Pokemon and One Piece card purchases. Free shipping over R1899. Secure checkout with fast delivery across South Africa." />
        <meta name="twitter:image" content="https://oracle-gaming.vercel.app/images/logo.svg" />
        <link rel="canonical" href="https://oracle-gaming.vercel.app/cart" />
      </Helmet>
      <Header />
      <div className="h-20" />
      <section className="gallery-carousel" style={{ paddingTop: '120px', paddingBottom: '30px' }}>
        <div className="container">
          <h2 className="h2 section-title" style={{ color: 'var(--white)' }}>YOUR CART</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8 overflow-hidden">
            <div className="lg:col-span-2 overflow-hidden">
              <div className="bg-primarySupport shadow-lg border border-black/20 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-black/20"><h2 className="text-xl font-semibold" style={{ color: 'var(--white)' }}>CART ITEMS ({cart.itemCount})</h2></div>
                <div className="divide-y divide-black/20">
                  {cart.items.map(item => (
                    <div key={item.id} className="p-4 sm:p-6 hover:bg-black/20 transition-colors">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6">
                        <div className="flex-shrink-0 flex justify-center sm:justify-start">
                          <img src={item.image || '/placeholder-product.jpg'} alt={item.name} className="w-20 h-20 sm:w-24 sm:h-24 object-cover shadow-sm" />
                        </div>
                        <div className="flex-1 min-w-0 text-center sm:text-left">
                          <h3 className="text-lg font-semibold mb-2 break-words" style={{ color: 'var(--white)' }}>{item.name}</h3>
                          {item.variationName && (
                            <p className="text-sm mb-1" style={{ color: 'var(--platinum)' }}>
                              <span className="font-medium">Variation:</span> {item.variationName}
                            </p>
                          )}
                          {item.bundleSelections && item.bundleSelections.length > 0 && (
                            <div className="text-sm mb-1" style={{ color: 'var(--platinum)' }}>
                              <span className="font-medium">Bundle includes:</span>
                              {item.bundleSelections.map((selection, index) => (
                                <div key={index} className="ml-2 text-xs">
                                  • {selection.variationName === 'Default option' 
                                      ? selection.productName 
                                      : `${selection.productName} - ${selection.variationName}`}
                                </div>
                              ))}
                            </div>
                          )}
                          <p className="font-medium text-lg" style={{ color: 'var(--white)' }}>{formatPrice(item.price)}</p>
                          <p className="text-sm" style={{ color: 'var(--platinum)' }}>Stock: {item.stockStatus === 'instock' ? 'In Stock' : 'Limited'}</p>
                        </div>
                        <div className="flex items-center justify-center sm:justify-end gap-3">
                          <button onClick={() => handleQuantityChange(item.productId, item.quantity - 1)} disabled={updatingItem === item.productId} className="w-10 h-10 flex items-center justify-center border-2 border-tertiary/40 hover:border-tertiary hover:bg-tertiary/10 disabled:opacity-50 transition-all"><Minus className="w-4 h-4" /></button>
                          <span className="w-16 text-center font-semibold text-lg">{updatingItem === item.productId ? <Loading size="sm" /> : item.quantity}</span>
                          <button onClick={() => handleQuantityChange(item.productId, item.quantity + 1)} disabled={updatingItem === item.productId} className="w-10 h-10 flex items-center justify-center border-2 border-tertiary/40 hover:border-tertiary hover:bg-tertiary/10 disabled:opacity-50 transition-all"><Plus className="w-4 h-4" /></button>
                        </div>
                        <div className="text-center sm:text-right">
                          <p className="text-xl font-bold" style={{ color: 'var(--white)' }}>{formatPrice(item.price * item.quantity)}</p>
                        </div>
                        <div className="flex justify-center sm:justify-end">
                          <button onClick={() => removeFromCart(item.productId)} className="text-tertiary/70 hover:text-red-500 transition-colors p-2" title="Remove item"><Trash2 className="w-5 h-5" /></button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Frequently Bought Together Section */}
              {crossSellProducts.length > 0 && (
                <div className="bg-primarySupport shadow-lg border border-black/20 mt-4">
                  <div className="p-4 sm:p-6 border-b border-black/20">
                    <h2 className="text-xl font-semibold" style={{ color: 'var(--white)' }}>
                      Frequently Bought Together
                    </h2>
                  </div>
                  <div className="p-4 sm:p-6">
                    {loadingCrossSells ? (
                      <div className="flex justify-center py-8">
                        <Loading size="md" text="Loading recommendations..." />
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {crossSellProducts.map((product) => (
                          <ProductCard
                            key={product.id}
                            product={product}
                            onViewDetails={(p) => navigate(`/shop/product/${p.id}`)}
                            className="transform transition-transform hover:scale-105"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="lg:col-span-1">
              <div className="bg-primarySupport shadow-lg border border-black/20 p-6 sticky top-24">
                <h2 className="text-xl font-semibold mb-6" style={{ color: 'var(--white)' }}>ORDER SUMMARY</h2>
                {hasPreOrderItems && (
                  <div className="mb-6 p-3 border" style={{ borderColor: 'var(--tertiary)', background: 'rgba(20, 254, 251, 0.08)' }}>
                    <p className="text-sm font-semibold" style={{ color: 'var(--tertiary)' }}>Pre-order in cart</p>
                    <p className="text-xs mt-1" style={{ color: 'var(--platinum)' }}>
                      Your order contains a pre-order item and will ship once all items are in stock.
                    </p>
                  </div>
                )}
                <div className="space-y-4 mb-6">
                  <div className="flex justify-between items-center"><span style={{ color: 'var(--platinum)' }}>Subtotal</span><span className="font-semibold" style={{ color: 'var(--white)' }}>{formatPrice(cart.total)}</span></div>
                  <div className="flex justify-between items-center">
                    <span style={{ color: 'var(--platinum)' }}>Shipping</span>
                    <span className={`font-semibold ${shipping.color}`}>
                      {shipping.text}
                    </span>
                  </div>
                  {cart.total < FREE_SHIPPING_THRESHOLD && (
                    <div className="text-sm" style={{ color: 'var(--platinum)' }}>
                      Add {formatPrice(FREE_SHIPPING_THRESHOLD - cart.total)} more for free shipping
                    </div>
                  )}
                  <div className="flex justify-between items-center"><span style={{ color: 'var(--platinum)' }}>Tax</span><span className="font-semibold" style={{ color: 'var(--white)' }}>Included</span></div>
                  <div className="border-t border-black/20 pt-4"><div className="flex justify-between items-center"><span className="text-xl font-bold" style={{ color: 'var(--white)' }}>Total</span><span className="text-xl font-bold" style={{ color: 'var(--white)' }}>{formatPrice(cart.total)}</span></div></div>
                </div>
                {cartError && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded">
                    <div className="flex items-center justify-between">
                      <p className="text-red-600 text-sm">{cartError}</p>
                      <button onClick={clearError} className="text-red-600 hover:text-red-800 text-sm">×</button>
                    </div>
                  </div>
                )}
                {checkoutError && (<div className="mb-6 p-4 bg-red-50 border border-red-200"><p className="text-red-600 text-sm">{checkoutError}</p></div>)}
                <div className="space-y-3">
                  <button onClick={handleCheckout} disabled={checkoutLoading || cart.items.length === 0} className="btn btn-primary" style={{ width: '100%' }}>{checkoutLoading ? (<Loading size="sm" text="Processing..." />) : (<> <CreditCard className="w-5 h-5" /> Proceed to Checkout</>)}</button>
                  <button onClick={clearCart} className="btn" style={{ width: '100%', border: '1px solid rgba(20, 254, 251, 0.4)', color: 'var(--white)' }}>Clear Cart</button>
                </div>
                <div className="mt-6 pt-6 border-t border-black/20">
                  <div className="text-center">
                    <p className="text-sm" style={{ color: 'var(--platinum)' }}>Secure Checkout</p>
                    <div className="flex justify-center items-center gap-4" style={{ color: 'var(--platinum)' }}>
                      <div className="flex items-center gap-1 text-xs"><CreditCard className="w-4 h-4" /> PayFast</div>
                      <div className="flex items-center gap-1 text-xs"><Package className="w-4 h-4" /> {cart.total >= FREE_SHIPPING_THRESHOLD ? 'Free Shipping' : 'Shipping Calculated at Checkout'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

